import { ReactNode, useRef } from 'react';
import { SnackbarProvider, SnackbarKey, MaterialDesignContent } from 'notistack';
import { alpha, styled, Theme, useTheme } from '@mui/material/styles';
import { Box, Collapse } from '@mui/material';

import { IconButtonAnimate } from './animate';
import { defaultSettings } from 'src/config';
import { IconCircleAlert, IconCircleCheck, IconInfo, IconTriangleAlert, IconX, LucideIcon } from 'src/libraries/icons';

//
//

const StyledMaterialDesignContent = styled(MaterialDesignContent)(({ theme, isLight }: {
  theme: Theme;
  isLight?: boolean;
}) => ({
  '&.notistack-MuiContent': {
    width: '100%',
    padding: theme.spacing(1, 2, 1, 1),
    margin: theme.spacing(0.25, 0),
    boxShadow: theme.customShadows.z8,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.grey[isLight ? 0 : 800],
    backgroundColor: theme.palette.grey[isLight ? 900 : 0],
    '&.SnackbarItem-variantSuccess, &.SnackbarItem-variantError, &.SnackbarItem-variantWarning, &.SnackbarItem-variantInfo':
      {
        color: 'green',
        backgroundColor: theme.palette.background.paper,
      },
    [theme.breakpoints.up('md')]: {
      minWidth: 240,
    },
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& #notistack-snackbar': {
    padding: 0,
  },
}));

//


type Props = {
  children: ReactNode;
};

export default function NotistackProvider({ children }: Props) {
  const isRTL = defaultSettings.themeDirection === 'rtl';

  const notistackRef = useRef<any>(null);

  const onClose = (key: SnackbarKey) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  //

  const theme = useTheme();


  return (
      <SnackbarProvider
        ref={notistackRef}
        dense
        maxSnack={5}
        preventDuplicate
        autoHideDuration={3000}
        TransitionComponent={isRTL ? Collapse : undefined}
        variant="success" // Set default variant
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        Components={{
          default: StyledMaterialDesignContent,
          success: StyledMaterialDesignContent,
          warning: StyledMaterialDesignContent,
          info: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
        iconVariant={{
          info: <SnackbarIcon Icon={IconInfo} color={theme.palette.info.main} />,
          success: <SnackbarIcon Icon={IconCircleCheck} color={theme.palette.success.main} />,
          warning: <SnackbarIcon Icon={IconTriangleAlert} color={theme.palette.warning.main} />,
          error: <SnackbarIcon Icon={IconCircleAlert} color={theme.palette.error.main} />,
        }}
        // With close as default
        action={(key) => (
          <IconButtonAnimate size="small" onClick={onClose(key)} sx={{ p: 0.5 }}>
            <IconX size={16} />
          </IconButtonAnimate>
        )}
      >
        {children}
      </SnackbarProvider>
  );
}

//

type SnackbarIconProps = {
  Icon: LucideIcon;
  color: string;
};

function SnackbarIcon({ Icon, color }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(color, 0.16),
      }}
    >
      <Icon color={color} size={20} />
    </Box>
  );
}
